import { useMemo, useState, useEffect, useCallback } from "react";
import { fetchDevOptions } from "../api.js";
const useDevOptions = () => {
  const isDevMode = useMemo(
    () => new URLSearchParams(window.location.search).get("isDev") === "true",
    []
  );
  const [serverDefaults, setServerDefaults] = useState(null);
  const isLoading = serverDefaults === null;
  useEffect(() => {
    let cancelled = false;
    fetchDevOptions().then((result) => {
      if (!cancelled && result) {
        setServerDefaults(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const [devOptionsExpanded, setDevOptionsExpanded] = useState(false);
  const [useTextRecallOverride, setUseTextRecall] = useState(null);
  const [pageSizeOverride, setPageSize] = useState(null);
  const [rankingWeightsOverride, setRankingWeights] = useState(null);
  const [useTargetedSectionTitlesOverride, setUseTargetedSectionTitles] = useState(null);
  const [useTwoPhaseFilteringOverride, setUseTwoPhaseFiltering] = useState(null);
  const [useOutlierFilteringOverride, setUseOutlierFiltering] = useState(null);
  const [ignoreRatingsOverride, setIgnoreRatings] = useState(null);
  const [ratingThresholdOverride, setRatingThreshold] = useState(null);
  const [llmModelOverride, setLlmModel] = useState(null);
  const [llmModelLightOverride, setLlmModelLight] = useState(null);
  const [searchTemperatureOverride, setSearchTemperature] = useState(null);
  const [maxPerBrandOverride, setMaxPerBrand] = useState(null);
  const [maxProductsForLLMOverride, setMaxProductsForLLM] = useState(null);
  const [numSearchAnglesOverride, setNumSearchAngles] = useState(null);
  const [targetCategoriesOverride, setTargetCategories] = useState(null);
  const [semanticThresholdOverride, setSemanticThreshold] = useState(null);
  const [productsPerSectionOverride, setProductsPerSection] = useState(null);
  const [productsBufferOverride, setProductsBuffer] = useState(null);
  const [angleStrategyOverride, setAngleStrategy] = useState(null);
  const [availabilityFilterOverride, setAvailabilityFilter] = useState(null);
  const [useOriginalQueryRerankingOverride, setUseOriginalQueryReranking] = useState(null);
  const [recatModelOverride, setRecatModel] = useState(null);
  const [analyzeModelOverride, setAnalyzeModel] = useState(null);
  const [planModelOverride, setPlanModel] = useState(null);
  const [promptLabelOverride, setPromptLabel] = useState(null);
  const useTextRecall = useTextRecallOverride ?? (serverDefaults == null ? void 0 : serverDefaults.useTextRecall) ?? false;
  const pageSize = pageSizeOverride ?? (serverDefaults == null ? void 0 : serverDefaults.pageSize) ?? 20;
  const rankingWeights = rankingWeightsOverride ?? (serverDefaults == null ? void 0 : serverDefaults.rankingWeights) ?? { semanticWeight: 0.4, salesWeight: 0.15, contentWeight: 0.1, reviewWeight: 0.15, rewardWeight: 0.1, priceWeight: 0.1 };
  const useTargetedSectionTitles = useTargetedSectionTitlesOverride ?? (serverDefaults == null ? void 0 : serverDefaults.useTargetedSectionTitles) ?? true;
  const useTwoPhaseFiltering = useTwoPhaseFilteringOverride ?? (serverDefaults == null ? void 0 : serverDefaults.useTwoPhaseFiltering) ?? true;
  const useOutlierFiltering = useOutlierFilteringOverride ?? (serverDefaults == null ? void 0 : serverDefaults.useOutlierFiltering) ?? true;
  const ignoreRatings = ignoreRatingsOverride ?? (serverDefaults == null ? void 0 : serverDefaults.ignoreRatings) ?? true;
  const ratingThreshold = ratingThresholdOverride ?? (serverDefaults == null ? void 0 : serverDefaults.ratingThreshold) ?? 2;
  const llmModel = llmModelOverride ?? (serverDefaults == null ? void 0 : serverDefaults.llmModel) ?? "gpt-4o";
  const llmModelLight = llmModelLightOverride ?? (serverDefaults == null ? void 0 : serverDefaults.llmModelLight) ?? "gpt-4o-mini";
  const searchTemperature = searchTemperatureOverride ?? (serverDefaults == null ? void 0 : serverDefaults.searchTemperature) ?? 0.5;
  const maxPerBrand = maxPerBrandOverride ?? (serverDefaults == null ? void 0 : serverDefaults.maxPerBrand) ?? 3;
  const maxProductsForLLM = maxProductsForLLMOverride ?? (serverDefaults == null ? void 0 : serverDefaults.maxProductsForLLM) ?? 50;
  const numSearchAngles = numSearchAnglesOverride ?? (serverDefaults == null ? void 0 : serverDefaults.numSearchAngles) ?? 6;
  const targetCategories = targetCategoriesOverride ?? (serverDefaults == null ? void 0 : serverDefaults.targetCategories) ?? 4;
  const semanticThreshold = semanticThresholdOverride ?? (serverDefaults == null ? void 0 : serverDefaults.semanticThreshold) ?? 0.65;
  const productsPerSection = productsPerSectionOverride ?? (serverDefaults == null ? void 0 : serverDefaults.productsPerSection) ?? 10;
  const productsBuffer = productsBufferOverride ?? (serverDefaults == null ? void 0 : serverDefaults.productsBuffer) ?? 15;
  const angleStrategy = angleStrategyOverride ?? (serverDefaults == null ? void 0 : serverDefaults.angleStrategy) ?? "auto";
  const availabilityFilter = availabilityFilterOverride ?? (serverDefaults == null ? void 0 : serverDefaults.availabilityFilter) ?? "apply";
  const useOriginalQueryReranking = useOriginalQueryRerankingOverride ?? (serverDefaults == null ? void 0 : serverDefaults.useOriginalQueryReranking) ?? true;
  const recatModel = recatModelOverride ?? (serverDefaults == null ? void 0 : serverDefaults.recatModel) ?? "";
  const analyzeModel = analyzeModelOverride ?? (serverDefaults == null ? void 0 : serverDefaults.analyzeModel) ?? "";
  const planModel = planModelOverride ?? (serverDefaults == null ? void 0 : serverDefaults.planModel) ?? "";
  const promptLabel = promptLabelOverride ?? (serverDefaults == null ? void 0 : serverDefaults.promptLabel) ?? "";
  const handleWeightChange = useCallback((key, newValue) => {
    setRankingWeights((prev) => {
      const current = prev ?? rankingWeights;
      const otherKeys = Object.keys(current).filter((k) => k !== key);
      const otherSum = otherKeys.reduce((sum, k) => sum + current[k], 0);
      const remaining = Math.max(0, 1 - newValue);
      const updated = { ...current, [key]: newValue };
      otherKeys.forEach((k) => {
        updated[k] = otherSum > 0 ? current[k] / otherSum * remaining : remaining / otherKeys.length;
      });
      return updated;
    });
  }, [rankingWeights]);
  const devOptions = useMemo(
    () => isDevMode ? {
      useTextRecall,
      pageSize,
      rankingWeights,
      useTargetedSectionTitles,
      useTwoPhaseFiltering,
      useOutlierFiltering,
      ignoreRatings,
      ratingThreshold,
      llmModel,
      llmModelLight,
      searchTemperature,
      maxPerBrand,
      maxProductsForLLM,
      numSearchAngles,
      targetCategories,
      semanticThreshold,
      productsPerSection,
      productsBuffer,
      angleStrategy,
      availabilityFilter,
      useOriginalQueryReranking,
      recatModel,
      analyzeModel,
      planModel,
      promptLabel
    } : void 0,
    [
      isDevMode,
      useTextRecall,
      pageSize,
      rankingWeights,
      useTargetedSectionTitles,
      useTwoPhaseFiltering,
      useOutlierFiltering,
      ignoreRatings,
      ratingThreshold,
      llmModel,
      llmModelLight,
      searchTemperature,
      maxPerBrand,
      maxProductsForLLM,
      numSearchAngles,
      targetCategories,
      semanticThreshold,
      productsPerSection,
      productsBuffer,
      angleStrategy,
      availabilityFilter,
      useOriginalQueryReranking,
      recatModel,
      analyzeModel,
      planModel,
      promptLabel
    ]
  );
  return {
    isDevMode,
    isLoading,
    devOptionsExpanded,
    setDevOptionsExpanded,
    useTextRecall,
    setUseTextRecall,
    pageSize,
    setPageSize,
    rankingWeights,
    setRankingWeights,
    handleWeightChange,
    useTargetedSectionTitles,
    setUseTargetedSectionTitles,
    useTwoPhaseFiltering,
    setUseTwoPhaseFiltering,
    useOutlierFiltering,
    setUseOutlierFiltering,
    ignoreRatings,
    setIgnoreRatings,
    ratingThreshold,
    setRatingThreshold,
    llmModel,
    setLlmModel,
    llmModelLight,
    setLlmModelLight,
    searchTemperature,
    setSearchTemperature,
    maxPerBrand,
    setMaxPerBrand,
    maxProductsForLLM,
    setMaxProductsForLLM,
    numSearchAngles,
    setNumSearchAngles,
    targetCategories,
    setTargetCategories,
    semanticThreshold,
    setSemanticThreshold,
    productsPerSection,
    setProductsPerSection,
    productsBuffer,
    setProductsBuffer,
    angleStrategy,
    setAngleStrategy,
    availabilityFilter,
    setAvailabilityFilter,
    useOriginalQueryReranking,
    setUseOriginalQueryReranking,
    recatModel,
    setRecatModel,
    analyzeModel,
    setAnalyzeModel,
    planModel,
    setPlanModel,
    promptLabel,
    setPromptLabel,
    devOptions
  };
};
export {
  useDevOptions
};
//# sourceMappingURL=useDevOptions.js.map
